/**
 * useOptimisticTickets Hook
 *
 * Provides instant UI feedback for Jira ticket creation and removal.
 *
 * When a vulnerability status changes to "Opened", this hook allows showing
 * a placeholder "Creating..." ticket immediately, before the API responds.
 * The placeholder is automatically replaced when the real ticket data arrives
 * via refetch.
 *
 * When disconnecting a ticket, it hides immediately with automatic rollback on error.
 *
 * Pattern inspired by React 19's useOptimistic:
 * - Immediate UI feedback (optimistic placeholder)
 * - Automatic sync with server state (placeholder replaced by real data)
 * - Simple mental model for consumers
 *
 * @example
 * ```tsx
 * const { tickets, addOptimisticTicket, removeOptimisticTicket, restoreOptimisticTicket } =
 *   useOptimisticTickets(serverTickets);
 *
 * // When status changes to "Opened"
 * async function handleStatusChange() {
 *   addOptimisticTicket(); // Shows placeholder immediately
 *   await updateStatus();   // API call
 *   await refetchTickets(); // Real ticket replaces placeholder
 * }
 *
 * // When disconnecting
 * async function handleDisconnect(ticketKey) {
 *   removeOptimisticTicket(ticketKey); // Hides immediately
 *   try {
 *     await disconnect();
 *   } catch {
 *     restoreOptimisticTicket(); // Rollback on error
 *   }
 * }
 * ```
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { Attribute } from '@/types';

/**
 * Creates an optimistic placeholder ticket for immediate UI feedback
 */
export function createOptimisticTicket(): Attribute {
  return {
    key: `optimistic-ticket-${Date.now()}`,
    username: '',
    source: '',
    name: 'jira',
    value: 'Creating...',
    status: 'A',
    created: new Date().toISOString(),
    capability: '',
    visited: '',
    ttl: 0,
    metadata: {
      status: 'Pending',
      resolution: '',
      link: '',
      assignee: '',
      isOptimistic: 'true',
    },
  };
}

interface UseOptimisticTicketsReturn {
  /** Combined server + optimistic tickets */
  tickets: Attribute[];
  /** Add an optimistic placeholder ticket */
  addOptimisticTicket: () => void;
  /** Whether there's currently an optimistic ticket in the list */
  hasOptimisticTicket: boolean;
  /** Remove a ticket optimistically (hides it immediately) */
  removeOptimisticTicket: (ticketKey: string) => void;
  /** Restore a removed ticket (for error rollback) */
  restoreOptimisticTicket: () => void;
}

/**
 * Hook for managing optimistic ticket state
 *
 * Provides optimistic UI pattern for ticket creation:
 * - Shows placeholder immediately when addOptimisticTicket is called
 * - Automatically clears placeholder when server tickets change
 *
 * @param serverTickets - The source of truth tickets from the API
 * @returns Object with tickets array, addOptimisticTicket function, and hasOptimisticTicket flag
 */
export function useOptimisticTickets(
  serverTickets: Attribute[]
): UseOptimisticTicketsReturn {
  // Local state for optimistic placeholder ticket
  const [optimisticTicket, setOptimisticTicket] = useState<Attribute | null>(
    null
  );

  // Track removed tickets for optimistic removal (key-based hiding)
  const [removedTicketKeys, setRemovedTicketKeys] = useState<Set<string>>(
    new Set()
  );

  // Store removed ticket for potential rollback
  const [lastRemovedTicket, setLastRemovedTicket] = useState<Attribute | null>(
    null
  );

  // Use ref to track previous count without triggering re-renders
  const prevServerCountRef = useRef(serverTickets.length);

  // Clear optimistic ticket when server tickets count increases (real ticket arrived)
  useEffect(() => {
    const currentCount = serverTickets.length;
    const prevCount = prevServerCountRef.current;

    if (currentCount > prevCount && optimisticTicket) {
      // Server ticket count increased, clear the optimistic placeholder
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptimisticTicket(null);
    }

    // Clear removed keys when server tickets count decreases (removal confirmed)
    if (currentCount < prevCount && removedTicketKeys.size > 0) {
      // Server confirmed removal, clear optimistic removal state
      setRemovedTicketKeys(new Set());
      setLastRemovedTicket(null);
    }

    // Update ref for next comparison
    prevServerCountRef.current = currentCount;
  }, [serverTickets.length, optimisticTicket, removedTicketKeys.size]);

  // Add a placeholder ticket for immediate UI feedback
  const addOptimisticTicket = () => {
    const newOptimisticTicket = createOptimisticTicket();
    setOptimisticTicket(newOptimisticTicket);
  };

  // Remove a ticket optimistically (hide it immediately)
  const removeOptimisticTicket = (ticketKey: string) => {
    // Find and store the ticket being removed for potential rollback
    const ticketToRemove = serverTickets.find(t => t.key === ticketKey);
    if (ticketToRemove) {
      setLastRemovedTicket(ticketToRemove);
    }

    // Add to removed set to filter it out
    setRemovedTicketKeys(prev => new Set([...prev, ticketKey]));
  };

  // Restore a removed ticket (for error rollback)
  const restoreOptimisticTicket = () => {
    if (lastRemovedTicket) {
      // Remove from removed keys set to make it visible again
      setRemovedTicketKeys(prev => {
        const updated = new Set(prev);
        updated.delete(lastRemovedTicket.key);
        return updated;
      });
      setLastRemovedTicket(null);
    }
  };

  // Combine server tickets with optimistic ticket, excluding removed ones
  const tickets = useMemo(() => {
    // Filter out removed tickets
    const visibleServerTickets = serverTickets.filter(
      ticket => !removedTicketKeys.has(ticket.key)
    );

    if (optimisticTicket) {
      return [...visibleServerTickets, optimisticTicket];
    }
    return visibleServerTickets;
  }, [serverTickets, optimisticTicket, removedTicketKeys]);

  // Check if there's an optimistic ticket
  const hasOptimisticTicket = optimisticTicket !== null;

  return {
    tickets,
    addOptimisticTicket,
    hasOptimisticTicket,
    removeOptimisticTicket,
    restoreOptimisticTicket,
  };
}
